import { useTranslation } from 'react-i18next';

const ICON_BASE_URL = 'https://ozhkjwcjsifdhfdexayd.supabase.co/storage/v1/object/public/website-images/icons2';

interface Service {
  id: string;
  title: string;
  description: string;
  features: string[];
  iconUrl: string;
}

interface PricingTier {
  id: string;
  name: string;
  price: string;
  period: string;
  description: string;
  features: string[];
  highlighted: boolean;
  ctaText: string;
}

function Services() {
  const { t } = useTranslation();

  const services: Service[] = [
    {
      id: 'web-design',
      title: t('services.webDesign.title'),
      description: t('services.webDesign.description'),
      features: [
        t('services.webDesign.feature1'),
        t('services.webDesign.feature2'),
        t('services.webDesign.feature3'),
        t('services.webDesign.feature4'),
      ],
      iconUrl: `${ICON_BASE_URL}/computer-front-color.png`,
    },
    {
      id: 'ecommerce',
      title: t('services.ecommerce.title'),
      description: t('services.ecommerce.description'),
      features: [
        t('services.ecommerce.feature1'),
        t('services.ecommerce.feature2'),
        t('services.ecommerce.feature3'),
        t('services.ecommerce.feature4'),
      ],
      iconUrl: `${ICON_BASE_URL}/3d-coin-front-color.png`,
    },
    {
      id: 'seo',
      title: t('services.seo.title'),
      description: t('services.seo.description'),
      features: [
        t('services.seo.feature1'),
        t('services.seo.feature2'),
        t('services.seo.feature3'),
        t('services.seo.feature4'),
      ],
      iconUrl: `${ICON_BASE_URL}/zoom-front-color.png`,
    },
    {
      id: 'hosting',
      title: t('services.hosting.title'),
      description: t('services.hosting.description'),
      features: [
        t('services.hosting.feature1'),
        t('services.hosting.feature2'),
        t('services.hosting.feature3'),
        t('services.hosting.feature4'),
      ],
      iconUrl: `${ICON_BASE_URL}/cube-front-color.png`,
    },
    {
      id: 'maintenance',
      title: t('services.maintenance.title'),
      description: t('services.maintenance.description'),
      features: [
        t('services.maintenance.feature1'),
        t('services.maintenance.feature2'),
        t('services.maintenance.feature3'),
        t('services.maintenance.feature4'),
      ],
      iconUrl: `${ICON_BASE_URL}/calculator-front-color.png`,
    },
    {
      id: 'branding',
      title: t('services.branding.title'),
      description: t('services.branding.description'),
      features: [
        t('services.branding.feature1'),
        t('services.branding.feature2'),
        t('services.branding.feature3'),
        t('services.branding.feature4'),
      ],
      iconUrl: `${ICON_BASE_URL}/chart-front-color.png`,
    },
  ];

  const pricingTiers: PricingTier[] = [
    {
      id: 'starter',
      name: t('pricing.starter.name'),
      price: t('pricing.starter.price'),
      period: t('pricing.starter.period'),
      description: t('pricing.starter.description'),
      features: [
        t('pricing.starter.feature1'),
        t('pricing.starter.feature2'),
        t('pricing.starter.feature3'),
        t('pricing.starter.feature4'),
        t('pricing.starter.feature5'),
      ],
      highlighted: false,
      ctaText: t('pricing.starter.cta'),
    },
    {
      id: 'professional',
      name: t('pricing.professional.name'),
      price: t('pricing.professional.price'),
      period: t('pricing.professional.period'),
      description: t('pricing.professional.description'),
      features: [
        t('pricing.professional.feature1'),
        t('pricing.professional.feature2'),
        t('pricing.professional.feature3'),
        t('pricing.professional.feature4'),
        t('pricing.professional.feature5'),
        t('pricing.professional.feature6'),
        t('pricing.professional.feature7'),
      ],
      highlighted: true,
      ctaText: t('pricing.professional.cta'),
    },
    {
      id: 'enterprise',
      name: t('pricing.enterprise.name'),
      price: t('pricing.enterprise.price'),
      period: t('pricing.enterprise.period'),
      description: t('pricing.enterprise.description'),
      features: [
        t('pricing.enterprise.feature1'),
        t('pricing.enterprise.feature2'),
        t('pricing.enterprise.feature3'),
        t('pricing.enterprise.feature4'),
        t('pricing.enterprise.feature5'),
        t('pricing.enterprise.feature6'),
        t('pricing.enterprise.feature7'),
        t('pricing.enterprise.feature8'),
      ],
      highlighted: false,
      ctaText: t('pricing.enterprise.cta'),
    },
  ];

  return (
    <div className="w-full">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-baby-blue-200 via-baby-blue-50 to-pink-200 px-4 py-16 md:py-20 lg:py-28 min-h-[300px] md:min-h-[350px] overflow-hidden">
        {/* Grid Pattern Background */}
        <div
          className="absolute inset-0 bg-grid-light"
          aria-hidden="true"
        />

        <div className="relative max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            {t('services.hero.title')}
          </h1>
          <p className="text-base md:text-lg lg:text-xl text-gray-700 max-w-3xl mx-auto mb-8">
            {t('services.hero.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="#pricing"
              className="inline-flex items-center justify-center w-full sm:w-auto h-12 px-8 bg-blue-400 hover:bg-blue-500 text-white font-semibold rounded-lg transition-colors duration-200"
            >
              {t('services.hero.viewPricing')}
            </a>
            <a
              href="#services-detail"
              className="inline-flex items-center justify-center w-full sm:w-auto h-12 px-8 bg-white hover:bg-gray-50 text-blue-400 font-semibold rounded-lg border-2 border-blue-400 transition-colors duration-200"
            >
              {t('services.hero.exploreServices')}
            </a>
          </div>
        </div>
      </section>

      {/* Services Detail Section */}
      <section id="services-detail" className="px-4 py-12 md:px-8 md:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('services.detail.title')}
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              {t('services.detail.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8">
            {services.map((service) => (
              <div
                key={service.id}
                className="bg-white border-2 border-gray-100 rounded-xl p-6 md:p-8 hover:border-blue-400 hover:shadow-xl transition-all duration-300 group"
              >
                <div className="mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-100 to-pink-100 rounded-lg flex items-center justify-center mb-4 group-hover:scale-110 transition-transform duration-300">
                    <img
                      src={service.iconUrl}
                      alt={service.title}
                      className="w-10 h-10"
                      crossOrigin="anonymous"
                    />
                  </div>
                  <h3 className="text-xl md:text-2xl font-bold text-gray-900 mb-3">
                    {service.title}
                  </h3>
                  <p className="text-base text-gray-600 mb-4">
                    {service.description}
                  </p>
                </div>

                <div className="space-y-3">
                  {service.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <img
                          src={`${ICON_BASE_URL}/bell-front-color.png`}
                          alt="Check"
                          className="w-5 h-5"
                          crossOrigin="anonymous"
                        />
                      </div>
                      <span className="text-sm md:text-base text-gray-700">{feature}</span>
                    </div>
                  ))}
                </div>

                <div className="mt-6 pt-6 border-t border-gray-100">
                  <a
                    href="#pricing"
                    className="inline-flex items-center text-blue-400 hover:text-blue-500 font-semibold text-sm md:text-base transition-colors duration-200"
                  >
                    {t('services.detail.learnMore')}
                    <img
                      src={`${ICON_BASE_URL}/pencil-front-color.png`}
                      alt="Arrow"
                      className="w-4 h-4 ml-2"
                      crossOrigin="anonymous"
                    />
                  </a>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="px-4 py-12 md:px-8 md:py-16 lg:py-20 bg-gradient-to-br from-blue-50 to-pink-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('services.whyChoose.title')}
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              {t('services.whyChoose.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <img
                  src={`${ICON_BASE_URL}/clock-front-color.png`}
                  alt="Fast delivery"
                  className="w-8 h-8"
                  crossOrigin="anonymous"
                />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                {t('services.whyChoose.fast.title')}
              </h3>
              <p className="text-sm md:text-base text-gray-600">
                {t('services.whyChoose.fast.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <img
                  src={`${ICON_BASE_URL}/dollar-front-color.png`}
                  alt="Affordable"
                  className="w-8 h-8"
                  crossOrigin="anonymous"
                />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                {t('services.whyChoose.affordable.title')}
              </h3>
              <p className="text-sm md:text-base text-gray-600">
                {t('services.whyChoose.affordable.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <img
                  src={`${ICON_BASE_URL}/bulb-front-color.png`}
                  alt="Bilingual"
                  className="w-8 h-8"
                  crossOrigin="anonymous"
                />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                {t('services.whyChoose.bilingual.title')}
              </h3>
              <p className="text-sm md:text-base text-gray-600">
                {t('services.whyChoose.bilingual.description')}
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg">
                <img
                  src={`${ICON_BASE_URL}/sheild-front-color.png`}
                  alt="Secure"
                  className="w-8 h-8"
                  crossOrigin="anonymous"
                />
              </div>
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-2">
                {t('services.whyChoose.secure.title')}
              </h3>
              <p className="text-sm md:text-base text-gray-600">
                {t('services.whyChoose.secure.description')}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="px-4 py-12 md:px-8 md:py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('pricing.title')}
            </h2>
            <p className="text-base md:text-lg text-gray-600 max-w-2xl mx-auto">
              {t('pricing.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 items-stretch">
            {pricingTiers.map((tier) => (
              <div
                key={tier.id}
                className={`relative rounded-2xl p-6 md:p-8 flex flex-col ${
                  tier.highlighted
                    ? 'bg-gradient-to-br from-blue-400 to-pink-400 text-white shadow-2xl scale-100 md:scale-105 border-4 border-blue-500'
                    : 'bg-white border-2 border-gray-200 hover:border-blue-400 hover:shadow-xl'
                } transition-all duration-300`}
              >
                {tier.highlighted && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-pink-500 text-white text-xs font-bold px-4 py-1 rounded-full uppercase tracking-wide">
                      {t('pricing.popular')}
                    </span>
                  </div>
                )}

                <div className="text-center mb-6">
                  <h3
                    className={`text-2xl md:text-3xl font-bold mb-2 ${
                      tier.highlighted ? 'text-white' : 'text-gray-900'
                    }`}
                  >
                    {tier.name}
                  </h3>
                  <p
                    className={`text-sm md:text-base mb-4 ${
                      tier.highlighted ? 'text-blue-50' : 'text-gray-600'
                    }`}
                  >
                    {tier.description}
                  </p>
                  <div className="flex items-baseline justify-center gap-2">
                    <span
                      className={`text-4xl md:text-5xl font-bold ${
                        tier.highlighted ? 'text-white' : 'text-gray-900'
                      }`}
                    >
                      {tier.price}
                    </span>
                    {tier.period && (
                      <span
                        className={`text-base md:text-lg ${
                          tier.highlighted ? 'text-blue-100' : 'text-gray-600'
                        }`}
                      >
                        {tier.period}
                      </span>
                    )}
                  </div>
                </div>

                <div className="space-y-4 mb-8 flex-grow">
                  {tier.features.map((feature, index) => (
                    <div key={index} className="flex items-start gap-3">
                      <div className="flex-shrink-0 mt-1">
                        <img
                          src={`${ICON_BASE_URL}/bell-front-color.png`}
                          alt="Check"
                          className="w-5 h-5"
                          crossOrigin="anonymous"
                        />
                      </div>
                      <span
                        className={`text-sm md:text-base ${
                          tier.highlighted ? 'text-white' : 'text-gray-700'
                        }`}
                      >
                        {feature}
                      </span>
                    </div>
                  ))}
                </div>

                <a
                  href="/contact"
                  className={`mt-auto block w-full h-12 flex items-center justify-center font-semibold rounded-lg transition-all duration-200 ${
                    tier.highlighted
                      ? 'bg-white text-blue-400 hover:bg-gray-100'
                      : 'bg-blue-400 text-white hover:bg-blue-500'
                  }`}
                >
                  {tier.ctaText}
                </a>
              </div>
            ))}
          </div>

          <div className="mt-12 md:mt-16 text-center">
            <p className="text-base md:text-lg text-gray-600 mb-6">
              {t('pricing.customNeed')}
            </p>
            <a
              href="/contact"
              className="inline-flex items-center justify-center w-full sm:w-auto h-12 px-8 bg-gradient-to-r from-blue-400 to-pink-400 hover:from-blue-500 hover:to-pink-500 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl"
            >
              {t('pricing.contactUs')}
            </a>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="px-4 py-12 md:px-8 md:py-16 lg:py-20 bg-gradient-to-br from-blue-50 to-pink-50">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12 md:mb-16">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('services.faq.title')}
            </h2>
            <p className="text-base md:text-lg text-gray-600">
              {t('services.faq.subtitle')}
            </p>
          </div>

          <div className="space-y-4 md:space-y-6">
            <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow duration-200">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">
                {t('services.faq.q1.question')}
              </h3>
              <p className="text-base text-gray-600">
                {t('services.faq.q1.answer')}
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow duration-200">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">
                {t('services.faq.q2.question')}
              </h3>
              <p className="text-base text-gray-600">
                {t('services.faq.q2.answer')}
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow duration-200">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">
                {t('services.faq.q3.question')}
              </h3>
              <p className="text-base text-gray-600">
                {t('services.faq.q3.answer')}
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 md:p-8 shadow-sm hover:shadow-md transition-shadow duration-200">
              <h3 className="text-lg md:text-xl font-bold text-gray-900 mb-3">
                {t('services.faq.q4.question')}
              </h3>
              <p className="text-base text-gray-600">
                {t('services.faq.q4.answer')}
              </p>
            </div>
          </div>

          <div className="mt-12 text-center">
            <p className="text-base md:text-lg text-gray-600 mb-4">
              {t('services.faq.stillHaveQuestions')}
            </p>
            <a
              href="/contact"
              className="inline-flex items-center justify-center w-full sm:w-auto h-12 px-8 bg-blue-400 hover:bg-blue-500 text-white font-semibold rounded-lg transition-colors duration-200"
            >
              {t('services.faq.contactSupport')}
            </a>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative px-4 py-12 md:px-8 md:py-16 lg:py-20 bg-gradient-to-r from-blue-400 to-pink-400 overflow-hidden">
        {/* Grid Pattern - subtle on colored background */}
        <div
          className="absolute inset-0"
          style={{
            backgroundImage: `
              linear-gradient(to right, rgba(255, 255, 255, 0.08) 1px, transparent 1px),
              linear-gradient(to bottom, rgba(255, 255, 255, 0.08) 1px, transparent 1px)
            `,
            backgroundSize: '32px 32px',
          }}
          aria-hidden="true"
        />

        <div className="relative max-w-4xl mx-auto text-center">
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
            {t('services.cta.title')}
          </h2>
          <p className="text-base md:text-lg lg:text-xl text-white mb-8">
            {t('services.cta.subtitle')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="/contact"
              className="inline-flex items-center justify-center w-full sm:w-auto h-12 px-8 bg-white hover:bg-gray-100 text-blue-400 font-semibold rounded-lg transition-colors duration-200 shadow-lg"
            >
              {t('services.cta.getStarted')}
            </a>
            <a
              href="/about"
              className="inline-flex items-center justify-center w-full sm:w-auto h-12 px-8 bg-transparent hover:bg-white/10 text-white font-semibold rounded-lg border-2 border-white transition-colors duration-200"
            >
              {t('services.cta.learnMore')}
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}

export default Services;
